import { jsPDF } from 'jspdf';
import { resolveSignatureDataUrl } from './signatureUtils';
import { W9FormData, AuditLogEntry } from '../types';

export async function generateW9Pdf(
  data: W9FormData,
  companyName: string,
  auditData?: {
    appId: string;
    signerEmail: string;
    ip: string;
    userAgent: string;
    events: AuditLogEntry[];
  }
): Promise<{ base64: string; blobUrl: string } | null> {
  try {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210;
    const ml = 14;
    const mr = 14;
    const uw = W - ml - mr; // 182mm
    let y = 14;

    const font = (style: 'normal' | 'bold' | 'italic' = 'normal', size = 8) => {
      pdf.setFont('helvetica', style);
      pdf.setFontSize(size);
    };

    const tx = (str: string, x: number, yy: number, opts?: any) => {
      pdf.text(str, x, yy, opts ?? {});
    };

    const hline = (yy: number, x1 = ml, x2 = W - mr) => {
      pdf.setDrawColor(80);
      pdf.setLineWidth(0.25);
      pdf.line(x1, yy, x2, yy);
    };

    // Header
    pdf.setFillColor(11, 34, 64); // #0b2240
    pdf.rect(ml, y, uw, 16, 'F');
    pdf.setTextColor(255, 255, 255);
    font('bold', 12);
    tx('W-9', ml + 4, y + 7);
    font('normal', 7.5);
    tx('Request for Taxpayer Identification Number and Certification', ml + 4, y + 12);

    font('bold', 10);
    tx('Armellini Logistics', W - mr - 4, y + 7, { align: 'right' });
    font('normal', 7);
    tx('Document Storage & Compliance', W - mr - 4, y + 12, { align: 'right' });

    y += 20;
    pdf.setTextColor(20, 20, 20);

    // Section 1: Taxpayer Identification
    font('bold', 8.5);
    pdf.setFillColor(240, 244, 248);
    pdf.rect(ml, y, uw, 6, 'F');
    tx('Part I: Taxpayer Information', ml + 2, y + 4.5);
    y += 9;

    font('normal', 7.5);
    tx('1. Name (as shown on your income tax return):', ml, y);
    font('bold', 8.5);
    tx(data.taxpayer.name || companyName, ml, y + 5);
    hline(y + 6.5);
    y += 10;

    font('normal', 7.5);
    tx('2. Business name/disregarded entity name, if different from above:', ml, y);
    font('bold', 8.5);
    tx(data.taxpayer.businessName || '—', ml, y + 5);
    hline(y + 6.5);
    y += 10;

    font('normal', 7.5);
    tx('3. Federal Tax Classification:', ml, y);
    font('bold', 8);
    const classLabel = data.taxpayer.taxClassification
      ? data.taxpayer.taxClassification.replace(/_/g, ' ').toUpperCase()
      : 'NOT SPECIFIED';
    tx(classLabel + (data.taxpayer.llcTaxClassification ? ` (${data.taxpayer.llcTaxClassification})` : ''), ml, y + 5);
    hline(y + 6.5);
    y += 10;

    font('normal', 7.5);
    tx('4. Address (number, street, and apt. or suite no.):', ml, y);
    font('bold', 8);
    tx(data.taxpayer.address || '—', ml, y + 5);
    hline(y + 6.5);
    y += 10;

    font('normal', 7.5);
    tx('5. City, state, and ZIP code:', ml, y);
    font('bold', 8);
    tx(`${data.taxpayer.city || ''}, ${data.taxpayer.state || ''} ${data.taxpayer.zip || ''}`.trim() || '—', ml, y + 5);
    hline(y + 6.5);
    y += 13;

    // Section 2: Taxpayer Identification Number (TIN)
    font('bold', 8.5);
    pdf.setFillColor(240, 244, 248);
    pdf.rect(ml, y, uw, 6, 'F');
    tx('Part II: Taxpayer Identification Number (TIN)', ml + 2, y + 4.5);
    y += 9;

    font('normal', 7.5);
    tx(`TIN Type: ${data.tin.tinType === 'ssn' ? 'Social Security Number (SSN)' : 'Employer Identification Number (EIN)'}`, ml, y);
    font('bold', 9);
    tx(`Taxpayer ID Number: ${data.tin.tin || '—'}`, ml, y + 5);
    hline(y + 7);
    y += 13;

    // Section 3: Certification
    font('bold', 8.5);
    pdf.setFillColor(240, 244, 248);
    pdf.rect(ml, y, uw, 6, 'F');
    tx('Part III: Certification & Electronic Signature', ml + 2, y + 4.5);
    y += 9;

    font('normal', 6.5);
    const certText = [
      'Under penalties of perjury, I certify that: (1) The number shown on this form is my correct taxpayer identification number;',
      '(2) I am not subject to backup withholding; (3) I am a U.S. citizen or other U.S. person; and (4) The FATCA code(s) entered',
      'on this form (if any) indicating that I am exempt from FATCA reporting is correct.'
    ];
    certText.forEach(line => {
      tx(line, ml, y);
      y += 3.5;
    });

    y += 4;
    font('bold', 7.5);
    tx(`Certification Accepted: ${data.certification.certificationAccepted ? '[X] YES' : '[ ] NO'}`, ml, y);
    y += 6;

    // Signature Box
    const sigBoxH = 22;
    pdf.setDrawColor(180);
    pdf.rect(ml, y, uw, sigBoxH);

    font('normal', 6.5);
    tx('Authorized Electronic Signature:', ml + 2, y + 4);
    tx(`Date Signed: ${data.certification.signedDate || new Date().toISOString().split('T')[0]}`, W - mr - 4, y + 4, { align: 'right' });

    if (data.certification.signature) {
      const sigData = resolveSignatureDataUrl(data.certification.signature, 60, 14);
      if (sigData) {
        try {
          pdf.addImage(sigData, 'PNG', ml + 4, y + 6, 55, 13);
        } catch (e) {
          font('italic', 10);
          tx(data.taxpayer.name || 'Authorized Signer', ml + 4, y + 14);
        }
      } else {
        font('italic', 10);
        tx(data.taxpayer.name || 'Authorized Signer', ml + 4, y + 14);
      }
    }

    y += sigBoxH + 6;

    // Audit trail stamp if present
    if (auditData) {
      font('bold', 7);
      pdf.setFillColor(245, 245, 245);
      pdf.rect(ml, y, uw, 16, 'F');
      pdf.setTextColor(100, 100, 100);
      tx('ELECTRONIC AUDIT TRAIL & TAMPER EVIDENCE', ml + 2, y + 4);
      font('normal', 6.5);
      tx(`Signer: ${auditData.signerEmail || 'customer'} | IP: ${auditData.ip || 'recorded'} | Timestamp: ${new Date().toISOString()}`, ml + 2, y + 8);
      tx(`User Agent: ${(auditData.userAgent || 'Web Browser').slice(0, 95)}`, ml + 2, y + 12);
    }

    const base64 = pdf.output('datauristring');
    const blob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(blob);

    return { base64, blobUrl };
  } catch (err) {
    console.error('Error generating W9 PDF:', err);
    return null;
  }
}
